import { Mail } from "lucide-react";
import { LegalLayout, LegalSection, LegalList, LegalSubhead } from "../components/LegalLayout";

const MailButton = () => (
  <a
    href="mailto:support@breelnk.click"
    data-testid="privacy-contact-email"
    className="group mt-5 inline-flex items-center gap-2.5 rounded-full bg-lime px-6 py-3.5 text-sm font-semibold text-limeText transition-transform hover:-translate-y-0.5"
  >
    <Mail size={16} />
    support@breelnk.click
  </a>
);

export const PrivacyPolicy = () => (
  <LegalLayout overline="Legal · BREELINK" title="Privacy Policy" titleTestId="privacy-title">
    <p className="mt-8 text-base leading-relaxed text-inkMuted">
      Welcome to the Privacy Policy page of Breelnk.click, owned and operated by ACH MEDIA INC. This
      page outlines how we collect, use, and protect your personal information in compliance with the
      General Data Protection Regulation (GDPR) and other applicable privacy laws. As a Micro SaaS
      company, we are committed to transparency, security, and respecting your rights as a user.
    </p>

    <LegalSection title="Scope of This Privacy Policy" testid="privacy-section-scope">
      <p>
        This Privacy Policy applies to all visitors, users, and others who interact with our website
        or services. By accessing or using Breelnk.click, you agree to the practices described in
        this policy.
      </p>
    </LegalSection>

    <LegalSection title="Information We Collect" testid="privacy-section-collect">
      <LegalSubhead>Personal Information</LegalSubhead>
      <p>We collect personal information that you provide to us, such as:</p>
      <LegalList
        items={[
          "Name",
          "Email address",
          "Payment details (processed securely through third-party payment processors)",
          "Any additional information you provide when creating an account or contacting us",
        ]}
      />
      <LegalSubhead>Usage Data</LegalSubhead>
      <p>We automatically collect technical data, including:</p>
      <LegalList items={["IP address", "Browser type", "Operating system", "Referrer URLs", "Time and date of access"]} />
      <LegalSubhead>Cookies and Tracking Technologies</LegalSubhead>
      <p>
        Our website uses cookies, pixels, and similar technologies to track your interaction and
        improve your experience. You can control cookie preferences through your browser settings.
      </p>
    </LegalSection>

    <LegalSection title="Legal Basis for Data Processing" testid="privacy-section-legal-basis">
      <p>We process your data under the following legal grounds:</p>
      <LegalList
        items={[
          "Consent — When you explicitly provide consent (e.g., subscribing to newsletters).",
          "Contractual Necessity — To fulfill our obligations in providing services.",
          "Legitimate Interests — For website optimization, analytics, and business improvements.",
          "Legal Obligation — To comply with applicable laws and regulations.",
        ]}
      />
    </LegalSection>

    <LegalSection title="How We Use Your Information" testid="privacy-section-use">
      <p>We use your information for:</p>
      <LegalSubhead>Providing Services</LegalSubhead>
      <LegalList
        items={[
          "Facilitating account creation and login processes.",
          "Delivering our SaaS tools and features effectively.",
        ]}
      />
      <LegalSubhead>Enhancing User Experience</LegalSubhead>
      <LegalList items={["Personalizing content and optimizing website performance."]} />
      <LegalSubhead>Marketing and Communication</LegalSubhead>
      <LegalList items={["Sending promotional messages, updates, or service-related announcements."]} />
      <LegalSubhead>Security and Compliance</LegalSubhead>
      <LegalList items={["Detecting and preventing fraud, unauthorized activities, and other risks."]} />
      <LegalSubhead>Analytics and Improvements</LegalSubhead>
      <LegalList items={["Understanding user behavior to improve our products and services."]} />
    </LegalSection>

    <LegalSection title="Your Rights Under GDPR" testid="privacy-section-rights">
      <p>As a user in the European Economic Area (EEA), you have the following rights under GDPR:</p>
      <LegalList
        items={[
          "Right to Access: Request a copy of your personal data.",
          "Right to Rectification: Correct inaccuracies in your data.",
          "Right to Erasure: Request deletion of your data (“Right to Be Forgotten”).",
          "Right to Restrict Processing: Limit the use of your data in certain cases.",
          "Right to Data Portability: Transfer your data to another service provider.",
          "Right to Object: Opt out of certain data processing activities, including marketing.",
        ]}
      />
      <p>
        To exercise your rights, contact us at{" "}
        <a href="mailto:support@breelnk.click" className="font-semibold text-ink underline decoration-lime decoration-2 underline-offset-2">
          support@breelnk.click
        </a>
        .
      </p>
    </LegalSection>

    <LegalSection title="Sharing Your Information" testid="privacy-section-sharing">
      <p>We do not sell your personal data. However, we may share your information with:</p>
      <LegalSubhead>Service Providers</LegalSubhead>
      <p>Payment processors, hosting providers, and analytics tools, who are contractually obligated to protect your data.</p>
      <LegalSubhead>Legal and Regulatory Authorities</LegalSubhead>
      <p>When required by law or to protect our rights and interests.</p>
      <LegalSubhead>Business Transfers</LegalSubhead>
      <p>In case of a merger, acquisition, or sale of assets, your data may be transferred to the new entity.</p>
    </LegalSection>

    <LegalSection title="Data Retention" testid="privacy-section-retention">
      <p>
        We retain your data only as long as necessary for the purposes outlined in this policy or as
        required by law. Upon expiration, data is securely deleted.
      </p>
    </LegalSection>

    <LegalSection title="Cookies and Tracking" testid="privacy-section-cookies">
      <p>Breelnk.click uses cookies to:</p>
      <LegalList items={["Track session activities.", "Provide personalized content.", "Analyze traffic and improve website performance."]} />
      <p>You may disable cookies through your browser settings, but doing so may affect the functionality of our website.</p>
    </LegalSection>

    <LegalSection title="Data Security" testid="privacy-section-security">
      <p>
        We employ industry-standard measures to protect your data, including encryption, firewalls,
        and access controls. While no system is completely secure, we continually review and update
        our practices.
      </p>
    </LegalSection>

    <LegalSection title="Third-Party Integrations" testid="privacy-section-integrations">
      <LegalSubhead>Payment Gateways</LegalSubhead>
      <p>All payments are processed securely through third-party providers, which comply with PCI-DSS standards.</p>
      <LegalSubhead>Analytics Tools</LegalSubhead>
      <p>We use services like Google Analytics to monitor site performance.</p>
      <LegalSubhead>Cloud Services</LegalSubhead>
      <p>Data may be stored and processed on servers located in countries outside your own, which comply with GDPR and other regulations.</p>
    </LegalSection>

    <LegalSection title="Children’s Privacy" testid="privacy-section-children">
      <p>
        Our website and services are not intended for children under the age of 13. We do not
        knowingly collect data from children. Contact us immediately if you believe a child has
        provided personal information.
      </p>
    </LegalSection>

    <LegalSection title="Changes to This Privacy Policy" testid="privacy-section-changes">
      <p>
        We may update this Privacy Policy from time to time. Changes will be effective upon posting on
        this page. We encourage you to review this policy regularly to stay informed.
      </p>
    </LegalSection>

    <div className="mt-14 rounded-3xl border border-line bg-surfaceAlt p-7 md:p-9" data-testid="privacy-contact">
      <h2 className="font-display text-xl font-semibold tracking-tight text-ink md:text-2xl">Contact Us</h2>
      <p className="mt-4 text-sm leading-relaxed text-inkMuted md:text-base">
        If you have any questions or concerns about this Privacy Policy or your data, please reach out
        to us:
      </p>
      <MailButton />
      <p className="mt-6 text-sm text-inkMuted">
        We are committed to protecting your privacy and providing a secure experience.
      </p>
    </div>
  </LegalLayout>
);
